"use client";
import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import { contactData } from "../app/(home)/data/contactData";
import SolarCalculatorPopup from "./SolarCalculatorPopUp";

const footerLinks = [
  { href: "/", label: "HOME", id: "hero" },
  {
    href: "#services",
    label: "SERVICES",
    id: "services",
    dropdown: [
      {
        href: "#residential-solar",
        label: "Residential Solar",
        id: "residential-solar",
      },
      {
        href: "#housing-society-solar",
        label: "Housing Society Solar",
        id: "housing-society-solar",
      },
      {
        href: "#commercial-solar",
        label: "Commercial Solar",
        id: "commercial-solar",
      },
    ],
  },
  { href: "/calculator", label: "SOLAR CALCULATOR", id: "calculator" },
  // { href: "/finance", label: "FINANCE", id: "finance" },
  { href: "/maintenance", label: "MAINTENANCE", id: "maintenance" },
  { href: "/careers", label: "CAREERS", id: "careers" },
  { href: "#blogs", label: "BLOGS", id: "blogs" },
  { href: "/eco-partner", label: "ECO PARTNERS", id: "eco-partner" },
  {
    href: "/channelpartners",
    label: "CHANNEL PARTNERS",
    id: "channelpartners",
  },
];

const socialLinks = [
  {
    href: "https://www.facebook.com/share/1BoQkbEcF4/?mibextid=wwXIfr",
    icon: Facebook,
    image: "/home/fb.png",
    alt: "Facebook",
  },
  {
    href: "https://www.instagram.com/satyanamsolar?igsh=MXR0aTV3ODRkOXliOA==",
    icon: Instagram,
    image: "/home/ig.png",
    alt: "Instagram",
  },
  {
    href: "https://www.linkedin.com/company/satyanam-solar-llp/",
    icon: Linkedin,
    image: "/home/ln.png",
    alt: "LinkedIn",
  },
];

const Footer = () => {
  const pathname = usePathname();


  const handleFooterLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    link: { href: string; id: string }
  ) => {
    if (link.href.startsWith("#")) {
      e.preventDefault();
      if (pathname !== "/") {
        window.location.href = "/";
        setTimeout(() => {
          const section = document.getElementById(link.href.replace("#", ""));
          if (section) {
            section.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        const section = document.getElementById(link.href.replace("#", ""));
        if (section) {
          section.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    // For other routes (/careers, /calculator, etc.), let Next.js handle the routing
  };

  return (
    <footer className="bg-black text-white pt-12 pb-4 px-4">
    {/* <footer className="bg-gray-800 text-white pt-12 pb-4 px-4"> */}
      <div className="container mx-auto flex flex-col justify-between items-center gap-8">
        {/* Logo and Company Info */}
        <div className="flex flex-col items-center gap-2">
          <Link href="/" className="flex flex-col items-center gap-1">
            <Image src="/logo.png" alt="Logo Image" width={60} height={60} />
            <div className="text-head1 text-desc-size3 font-sans tracking-[.18em]">
              SATYANAM SOLAR
            </div>
          </Link>
          <p className="text-center text-desc-size2 font-sans text-gray-400">
            {/* Empowering your future with sustainable energy solutions. */}
            Powering homes not your bills.
          </p>
        </div>

        {/* Footer Links */}
        <div className="flex flex-col gap-6 items-center">
          <div className="flex flex-col gap-1 items-center">
            <h3 className="text-head2 text-desc-size2 font-robo font-bold">
              Quick Links
            </h3>
            {footerLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`text-desc-size2 font-sans hover:text-yellow-500 text-gray-300`}
                onClick={(e) => handleFooterLinkClick(e, link)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* contact Us */}
        <div className="flex flex-col gap-2 items-center">
          <h3 className="text-head2 text-desc-size2 font-robo font-bold">
            Contact Us
          </h3>
          <div className="flex flex-row gap-2 items-center">
            <Phone size={20} className="text-color4" />
            <a
              href={`tel:${contactData.contactInfo.phone}`}
              className="text-desc-size2 hover:underline"
            >
              {contactData.contactInfo.phone}
            </a>
          </div>

          <div className="flex flex-row gap-2 items-center">
            <Mail size={20} className="text-color4" />
            <a
              href={`mailto:${contactData.contactInfo.email}`}
              className="text-desc-size2 hover:underline"
            >
              {contactData.contactInfo.email}
            </a>
          </div>

          <div className="flex flex-row gap-2 items-center">
            <MapPin size={20} className="text-color4" />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                contactData.contactInfo.address
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-desc-size2 hover:underline"
            >
              {contactData.contactInfo.address}
            </a>
          </div>
        </div>

        {/* Social Links */}
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <Link
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  aria-label="Social Link"
                  rel="noopener noreferrer"
                  className="hover:opacity-75 w-8 h-8 rounded-full bg-hex bg-opacity-60 flex items-center justify-center"
                >
                  {/* <Icon className="w-6 h-6 text-[#D65342]" /> */}
                  <Image src={social.image} alt="Logo Image" width={20} height={20}/>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      {/* Copyright */}
      <div className="text-center mt-8 text-desc-size2 font-robo text-gray-400">
        &copy; {new Date().getFullYear()} Satyanam Solar. All rights reserved.
      </div>
    </footer>
  );
};

export default Footer;
